import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { NavItem } from '../types';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: '📊' },
  { label: 'Catalogue', path: '/catalogue', icon: '📚' },
  { label: 'Orders', path: '/orders', icon: '📦' },
  { label: 'Analytics', path: '/analytics', icon: '📈' },
  { label: 'Reviews', path: '/reviews', icon: '⭐' },
  { label: 'Messages', path: '/messages', icon: '💬' },
  { label: 'Notifications', path: '/notifications', icon: '🔔' },
  { label: 'Settings', path: '/settings', icon: '⚙️' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Shop Banner */}
      <div className={`px-4 pt-6 pb-4 border-b border-teal-800/30 ${collapsed ? 'items-center' : ''}`}>
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-10 h-10 rounded-xl bg-emerald/20 border border-emerald/30 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">🏪</span>
          </div>
          {!collapsed && (
            <div className="min-w-0 animate-fade-in">
              <h2 className="text-sm font-bold text-white truncate">
                {user?.business_name || 'My Shop'}
              </h2>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-yellow-400 text-xs">⭐</span>
                <span className="text-xs text-teal-200 font-medium">4.8 rating</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onMobileClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
              ${collapsed ? 'justify-center' : ''}
              ${isActive
                ? 'bg-white/15 text-white shadow-sm'
                : 'text-teal-200 hover:text-white hover:bg-white/8'
              }`
            }
          >
            <span className="text-base flex-shrink-0">{item.icon}</span>
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-teal-800/30 p-3 space-y-1">
        {/* User */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-teal-200 
                        hover:text-white hover:bg-white/8 transition-all duration-200 w-full
                        ${collapsed ? 'justify-center' : ''}`}
          >
            <div className="w-7 h-7 rounded-lg bg-emerald/30 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">
                {user?.name?.charAt(0)?.toUpperCase() || '👤'}
              </span>
            </div>
            {!collapsed && (
              <span className="truncate">{user?.name || 'Seller'}</span>
            )}
          </button>

          {showUserMenu && !collapsed && (
            <div className="absolute bottom-full left-3 right-3 mb-1 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-slide-up">
              <button
                onClick={() => { setShowUserMenu(false); navigate('/settings'); onMobileClose(); }}
                className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-cream-50 text-left font-medium transition-colors"
              >
                👤 Profile
              </button>
              <button
                onClick={() => { setShowUserMenu(false); handleLogout(); }}
                className="w-full px-4 py-2.5 text-sm text-coral hover:bg-coral-50 text-left font-medium transition-colors"
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>

        {/* Logout (always visible when collapsed) */}
        {collapsed && (
          <button
            onClick={handleLogout}
            className="flex items-center justify-center px-3 py-2.5 rounded-xl text-sm font-medium 
                       text-coral-300 hover:text-coral hover:bg-white/8 transition-all duration-200 w-full"
          >
            <span className="text-base">🚪</span>
          </button>
        )}
      </div>

      {/* Collapse Toggle (desktop only) */}
      <div className="hidden lg:block border-t border-teal-800/30 p-3">
        <button
          onClick={onToggle}
          className="flex items-center justify-center w-full px-3 py-2 rounded-xl text-teal-300 
                     hover:text-white hover:bg-white/8 transition-all duration-200"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden drawer-overlay"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-teal-900 z-50 lg:hidden transform transition-transform duration-300 ease-out
          ${mobileOpen ? 'translate-x-0 drawer-slide' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-end p-3">
          <button
            onClick={onMobileClose}
            className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>
        {sidebarContent}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-teal-900 sidebar-transition flex-shrink-0
          ${collapsed ? 'w-[72px]' : 'w-64'}`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
